import {
  OfficeLayout,
  TimeMachineFrame,
  TimeMachineIncident,
  TimeMachineEvent,
  AIAgent,
  SimulationStats,
} from '../types';

/**
 * Generates realistic seed history frames with rich causal chains,
 * specifically reconstructing the 14:32 incident requested by the user:
 * Task assigned -> Inter-agent conversation -> MCP Server Call ->
 * Context received -> Agent decision -> Operation error / Failure.
 */
export function generateSeedTimeMachineHistory(baseLayout: OfficeLayout): {
  frames: TimeMachineFrame[];
  incidents: TimeMachineIncident[];
} {
  const agents = baseLayout.agents || [];
  const backendAgent = agents.find((a) => a.role === 'backend') || agents[0] || {
    id: 'backend_default',
    name: 'Alex Rivera',
    nickname: 'Alex Backend',
    role: 'backend',
    currentCol: 4,
    currentRow: 4,
    avatarShirtColor: '#3b82f6',
    hairColor: '#475569',
    energy: 65,
    bladder: 30,
    focus: 85,
    status: 'working',
    statusTimer: 0,
    renderX: 0,
    renderY: 0,
    targetCol: null,
    targetRow: null,
    path: [],
    taskQueue: [],
    processedTasksCount: 12,
    avgResponseTimeSec: 2.1,
    systemPrompt: '',
    thoughts: [],
  };

  const devopsAgent = agents.find((a) => a.role === 'devops') || agents[1] || {
    id: 'devops_default',
    name: 'Sam Chen',
    nickname: 'Sam DevOps',
    role: 'devops',
    currentCol: 7,
    currentRow: 4,
    avatarShirtColor: '#10b981',
    hairColor: '#1e293b',
    energy: 78,
    bladder: 25,
    focus: 90,
    status: 'working',
    statusTimer: 0,
    renderX: 0,
    renderY: 0,
    targetCol: null,
    targetRow: null,
    path: [],
    taskQueue: [],
    processedTasksCount: 18,
    avgResponseTimeSec: 1.8,
    systemPrompt: '',
    thoughts: [],
  };

  const frontendAgent = agents.find((a) => a.role === 'frontend') || agents[2] || {
    id: 'frontend_default',
    name: 'Elena Rostova',
    nickname: 'Elena Frontend',
    role: 'frontend',
    currentCol: 4,
    currentRow: 7,
    avatarShirtColor: '#ec4899',
    hairColor: '#92400e',
    energy: 55,
    bladder: 45,
    focus: 70,
    status: 'working',
    statusTimer: 0,
    renderX: 0,
    renderY: 0,
    targetCol: null,
    targetRow: null,
    path: [],
    taskQueue: [],
    processedTasksCount: 15,
    avgResponseTimeSec: 2.4,
    systemPrompt: '',
    thoughts: [],
  };

  const baseStats: SimulationStats = {
    totalTasksCompleted: 38,
    tasksInQueue: 4,
    throughputTasksPerMin: 14.2,
    avgSlaLatencySec: 2.1,
    officeEfficiencyPct: 94,
    activeBottlenecks: [],
    history: [],
  };

  const rawTimestamps = [
    { clockTime: '14:28:00', deltaSec: -244 },
    { clockTime: '14:29:15', deltaSec: -169 },
    { clockTime: '14:30:10', deltaSec: -114 },
    { clockTime: '14:31:05', deltaSec: -59 },
    { clockTime: '14:31:40', deltaSec: -24 },
    { clockTime: '14:32:00', deltaSec: -4 },
    { clockTime: '14:32:04', deltaSec: 0, isKeyIncident: true }, // The core 14:32 failure
    { clockTime: '14:32:45', deltaSec: 41 },
    { clockTime: '14:33:30', deltaSec: 86 },
    { clockTime: '14:34:20', deltaSec: 136 },
    { clockTime: '14:35:10', deltaSec: 186 },
  ];

  const now = Date.now();
  const frames: TimeMachineFrame[] = [];

  rawTimestamps.forEach((tData, idx) => {
    const timestamp = now + tData.deltaSec * 1000;
    const isIncident = Boolean(tData.isKeyIncident);

    // Deep clone agents for this historical instant
    const frameAgents: AIAgent[] = JSON.parse(JSON.stringify(agents));

    // Custom agent state choreography for the 14:32 storyline
    const agBackend = frameAgents.find((a) => a.id === backendAgent.id);
    const agDevops = frameAgents.find((a) => a.id === devopsAgent.id);
    const agFrontend = frameAgents.find((a) => a.id === frontendAgent.id);

    const frameEvents: TimeMachineEvent[] = [];
    let activeDialogues: { fromAgentId: string; toAgentId: string; message: string }[] | undefined;

    if (tData.clockTime === '14:28:00') {
      if (agFrontend) {
        agFrontend.status = 'coffee_break';
        agFrontend.thoughts = [{ time: '14:28', text: 'Having coffee in the breakroom ☕', mood: 'break' }];
      }
      frameEvents.push({
        id: `ev_init_${idx}`,
        timestamp,
        clockTime: tData.clockTime,
        type: 'break',
        title: 'Scheduled Coffee Break',
        description: `${frontendAgent.name} walked over to the breakroom to recharge energy.`,
        agentId: frontendAgent.id,
        agentName: frontendAgent.name,
      });
    } else if (tData.clockTime === '14:29:15') {
      if (agFrontend) {
        agFrontend.status = 'working';
        agFrontend.energy = 98;
      }
      frameEvents.push({
        id: `ev_comp_${idx}`,
        timestamp,
        clockTime: tData.clockTime,
        type: 'task_completed',
        title: 'UI v2.4 Deployment Completed',
        description: `${frontendAgent.name} successfully finalized Dashboard components.`,
        agentId: frontendAgent.id,
        agentName: frontendAgent.name,
        severity: 'success',
      });
    } else if (tData.clockTime === '14:30:10') {
      // Step 1: Task Received
      if (agBackend) {
        agBackend.status = 'working';
        agBackend.thoughts = [{ time: '14:30', text: 'New task: DB Schema Migration', mood: 'busy' }];
        agBackend.taskQueue = [
          {
            id: 'task_db_sync_01',
            title: 'Schema Migration & Transactional Sync v2.4',
            type: 'Database',
            assignedAgentId: backendAgent.id,
            progress: 15,
            createdAt: timestamp,
            complexity: 'hard',
            status: 'in_progress',
          },
        ];
      }
      frameEvents.push({
        id: `ev_task_${idx}`,
        timestamp,
        clockTime: tData.clockTime,
        type: 'task_assign',
        title: '📥 Critical Task Assigned',
        description: `${backendAgent.name} received task "Schema Migration & Transactional Sync v2.4".`,
        agentId: backendAgent.id,
        agentName: backendAgent.name,
        contextData: '{"target_table": "user_transactions", "rows_count": 1450000, "schema_version": "2.4.0"}',
        severity: 'info',
      });
    } else if (tData.clockTime === '14:31:05') {
      // Step 2: Agent-to-Agent collaboration dialogue
      if (agBackend && agDevops) {
        agBackend.thoughts = [{ time: '14:31', text: 'Checking cluster state with Sam...', mood: 'busy' }];
        agDevops.thoughts = [{ time: '14:31', text: 'Verifying Postgres read replicas...', mood: 'busy' }];
      }
      activeDialogues = [
        {
          fromAgentId: backendAgent.id,
          toAgentId: devopsAgent.id,
          message: 'Is the replica cluster ready for partition migration?',
        },
      ];
      frameEvents.push({
        id: `ev_chat_${idx}`,
        timestamp,
        clockTime: tData.clockTime,
        type: 'agent_chat',
        title: '💬 Inter-Agent Synchronization',
        description: `${backendAgent.name} checked primary cluster availability with ${devopsAgent.name}.`,
        agentId: backendAgent.id,
        agentName: backendAgent.name,
        targetAgentId: devopsAgent.id,
        targetAgentName: devopsAgent.name,
        contextData: 'Internal RPC Channel: #devops-infra (Agent-to-Agent Protocol)',
        severity: 'info',
      });
    } else if (tData.clockTime === '14:31:40') {
      // Step 3: MCP Server Call
      if (agBackend) {
        agBackend.thoughts = [{ time: '14:31', text: 'Calling postgres-gateway MCP Server...', mood: 'busy' }];
      }
      frameEvents.push({
        id: `ev_mcp_${idx}`,
        timestamp,
        clockTime: tData.clockTime,
        type: 'mcp_call',
        title: '🔌 MCP Server Invocation',
        description: `${backendAgent.name} invoked tool mcp://postgres-gateway/execute_migration.`,
        agentId: backendAgent.id,
        agentName: backendAgent.name,
        mcpServer: 'mcp://postgres-gateway.internal:8080',
        mcpMethod: 'execute_migration',
        mcpPayload: {
          statement: 'ALTER TABLE user_transactions ADD COLUMN security_hash VARCHAR(64);',
          lock_mode: 'EXCLUSIVE_LOCK',
          timeout_ms: 3000,
        },
        severity: 'warning',
      });
    } else if (tData.clockTime === '14:32:00') {
      // Step 4 & 5: Context Injected & Autonomous Decision
      if (agBackend) {
        agBackend.thoughts = [{ time: '14:32', text: '⚠️ Lock conflict detected. Forcing NOWAIT mode...', mood: 'tired' }];
      }
      frameEvents.push({
        id: `ev_ctx_${idx}`,
        timestamp,
        clockTime: tData.clockTime,
        type: 'context_inject',
        title: '🧠 Context Injected by MCP Server',
        description: 'The MCP Server reported 48 active concurrent transactions on target table.',
        agentId: backendAgent.id,
        agentName: backendAgent.name,
        contextData: '{"active_transactions": 48, "lock_status": "WAITING_LOCK", "lock_holder_pid": 9842}',
        severity: 'warning',
      });
      frameEvents.push({
        id: `ev_dec_${idx}`,
        timestamp,
        clockTime: tData.clockTime,
        type: 'decision',
        title: '💡 Autonomous Agent Decision',
        description: `${backendAgent.name} decided to force immediate acquisition without exponential backoff to meet SLA.`,
        agentId: backendAgent.id,
        agentName: backendAgent.name,
        agentDecision: 'Force immediate NOWAIT lock acquisition instead of staged exponential backoff.',
        severity: 'warning',
      });
    } else if (tData.clockTime === '14:32:04') {
      // Step 6: CRITICAL ERROR & INCIDENT TRIGGERED
      if (agBackend) {
        agBackend.status = 'blocked';
        agBackend.focus = 25;
        agBackend.thoughts = [{ time: '14:32', text: '🚨 ERROR 500! MCP Timeout & Deadlock', mood: 'tired' }];
      }
      if (agDevops) {
        agDevops.thoughts = [{ time: '14:32', text: '🚨 Cluster alert: transactions blocked', mood: 'tired' }];
      }
      frameEvents.push({
        id: `ev_err_${idx}`,
        timestamp,
        clockTime: tData.clockTime,
        type: 'error',
        title: '🚨 Operation Failed: MCP Connection Timeout',
        description: 'MCP server dropped connection due to exceeded timeout threshold (>3000ms). Transaction aborted.',
        agentId: backendAgent.id,
        agentName: backendAgent.name,
        mcpServer: 'mcp://postgres-gateway.internal:8080',
        errorDetails: '504 Gateway Timeout: Exclusive lock timeout after 3002ms. Transaction rolled back automatically.',
        severity: 'error',
      });
    } else if (tData.clockTime === '14:32:45') {
      if (agDevops) {
        agDevops.status = 'working';
        agDevops.thoughts = [{ time: '14:32', text: 'Initiating connection draining and recycling...', mood: 'busy' }];
      }
      frameEvents.push({
        id: `ev_diag_${idx}`,
        timestamp,
        clockTime: tData.clockTime,
        type: 'mcp_call',
        title: '🔧 Auto-Recovery Script Execution',
        description: `${devopsAgent.name} executed mcp://k8s-infra/flush_idle_connections.`,
        agentId: devopsAgent.id,
        agentName: devopsAgent.name,
        mcpServer: 'mcp://k8s-infra.internal',
        mcpMethod: 'flush_idle_connections',
        severity: 'info',
      });
    } else if (tData.clockTime === '14:33:30') {
      if (agBackend) {
        agBackend.status = 'working';
        agBackend.thoughts = [{ time: '14:33', text: 'Cluster stabilized. Retrying with safe batches...', mood: 'happy' }];
      }
      frameEvents.push({
        id: `ev_recov_${idx}`,
        timestamp,
        clockTime: tData.clockTime,
        type: 'decision',
        title: '✨ Recovery Policy Applied',
        description: 'System applied 1,000-record batch partitioning with 50ms interval pauses.',
        agentId: backendAgent.id,
        agentName: backendAgent.name,
        severity: 'success',
      });
    } else {
      frameEvents.push({
        id: `ev_normal_${idx}`,
        timestamp,
        clockTime: tData.clockTime,
        type: 'task_completed',
        title: 'System Operating at Peak Health',
        description: 'All queues synchronized and latency within standard bounds (1.8s).',
        severity: 'success',
      });
    }

    const frameStats: SimulationStats = {
      ...baseStats,
      tasksInQueue: isIncident ? 9 : tData.clockTime === '14:32:00' ? 6 : 3,
      avgSlaLatencySec: isIncident ? 4.9 : tData.clockTime === '14:32:00' ? 3.4 : 2.1,
      officeEfficiencyPct: isIncident ? 58 : tData.clockTime === '14:32:00' ? 74 : 94,
      activeBottlenecks: isIncident
        ? [
            {
              agentName: backendAgent.name,
              reason: 'Blocked in MCP Server call (Postgres Gateway)',
              queueLength: 5,
            },
          ]
        : [],
    };

    frames.push({
      id: `frame_${idx}_${tData.clockTime.replace(':', '_')}`,
      timestamp,
      clockTime: tData.clockTime,
      layoutSnapshot: {
        ...baseLayout,
        agents: frameAgents,
      },
      statsSnapshot: frameStats,
      events: frameEvents,
      isIncident,
      incidentSummary: isIncident
        ? '🚨 14:32:04 - MCP Server Failure (Postgres Gateway Timeout)'
        : undefined,
      incidentId: isIncident ? 'incident_14_32_mcp_failure' : undefined,
      activeAgentDialogues: activeDialogues,
    });
  });

  const incidents: TimeMachineIncident[] = [
    {
      id: 'incident_14_32_mcp_failure',
      timestamp: now,
      clockTime: '14:32:04',
      title: '🚨 Critical MCP Operation Failure (Postgres Gateway Timeout)',
      agentName: backendAgent.name,
      role: 'Backend Senior Agent',
      category: 'mcp_error',
      summary:
        'At 14:32:04, the database schema migration failed because the agent forced an exclusive lock without backoff in the presence of 48 active concurrent transactions on the MCP server.',
      causalChain: [
        {
          stepNumber: 1,
          stepTitle: '📥 High-Priority Task Received',
          stepType: 'task',
          timestamp: '14:30:10',
          agent: backendAgent.name,
          detail: 'Agent receives instruction to execute schema migration on table user_transactions containing 1.45M rows.',
          rawPayload: {
            task: 'Schema Migration & Transactional Sync v2.4',
            priority: 'HIGH',
            deadline: '14:35:00',
          },
        },
        {
          stepNumber: 2,
          stepTitle: '💬 Inter-Agent Synchronization (DevOps)',
          stepType: 'chat',
          timestamp: '14:31:05',
          agent: `${backendAgent.name} ➔ ${devopsAgent.name}`,
          detail: 'Replica cluster verification inquiry before deployment rollout.',
          rawPayload: {
            message: 'Is the replica cluster ready for partition migration?',
            channel: 'mcp-agent-rpc',
          },
        },
        {
          stepNumber: 3,
          stepTitle: '🔌 MCP Server Invocation (postgres-gateway)',
          stepType: 'mcp',
          timestamp: '14:31:40',
          agent: backendAgent.name,
          detail: 'Remote call to endpoint mcp://postgres-gateway.internal:8080/execute_migration requesting exclusive lock.',
          rawPayload: {
            server: 'mcp://postgres-gateway.internal:8080',
            method: 'execute_migration',
            lock_mode: 'EXCLUSIVE_LOCK',
            timeout_ms: 3000,
          },
        },
        {
          stepNumber: 4,
          stepTitle: '🧠 Context Received from MCP Server',
          stepType: 'context',
          timestamp: '14:32:00',
          agent: backendAgent.name,
          detail: 'MCP server responds warning of 48 ongoing concurrent transactions holding the lock.',
          rawPayload: {
            status: 'WAITING_LOCK',
            active_transactions: 48,
            estimated_wait_ms: 4200,
          },
        },
        {
          stepNumber: 5,
          stepTitle: '💡 Autonomous Agent Decision (LLM Reasoning)',
          stepType: 'decision',
          timestamp: '14:32:02',
          agent: backendAgent.name,
          detail: 'To prevent SLA degradation, agent decides to force lock acquisition via NOWAIT flag instead of queueing.',
          rawPayload: {
            reasoning: 'Critical SLA. Retry with immediate forced NOWAIT lock.',
            confidence: 0.72,
          },
        },
        {
          stepNumber: 6,
          stepTitle: '❌ Operation Failure & Automatic Rollback',
          stepType: 'error',
          timestamp: '14:32:04',
          agent: backendAgent.name,
          detail: 'MCP server rejects request after exceeding 3000ms timeout. Transaction aborted with 504 Gateway Timeout exception.',
          rawPayload: {
            code: 504,
            error: 'Exclusive lock timeout after 3002ms. Transaction rolled back.',
            impact: 'Deployment pipeline blocked.',
          },
        },
      ],
      rootCause:
        'The backend agent decided to force an immediate exclusive lock (NOWAIT) while 48 concurrent transactions were active on the MCP server, exceeding the 3-second timeout threshold.',
      recommendedFix:
        'Configure the agent system prompt and policy to use CONCURRENTLY batch migrations and enable exponential backoff with jitter whenever more than 10 active transactions are detected.',
      frameIndex: 6,
    },
  ];

  return { frames, incidents };
}
